import React from 'react'
import { Button, HStack, Text, Box, Flex } from '@chakra-ui/react'

type RatingValue = 1 | 2 | 3 | 4 | 5

const RATING_STYLES: Record<
  RatingValue,
  { bg: string; border: string; color: string }
> = {
  1: { bg: '#FCEBEB', border: '#E24B4A', color: '#791F1F' },
  2: { bg: '#FAEEDA', border: '#EF9F27', color: '#633806' },
  3: { bg: '#FAEEDA', border: '#BA7517', color: '#412402' },
  4: { bg: '#EAF3DE', border: '#639922', color: '#27500A' },
  5: { bg: '#EAF3DE', border: '#1D9E75', color: '#085041' },
}

const FEEDBACK_MESSAGES: Record<RatingValue, string> = {
  1: "We're sorry to hear that. Your feedback helps improve the clinic experience.",
  2: 'Thank you \u2014 your feedback has been noted.',
  3: 'Thank you \u2014 your feedback has been noted.',
  4: 'Great to hear! Your feedback has been recorded.',
  5: 'Wonderful! So glad the visit went well. Your feedback has been recorded.',
}

interface RatingInputProps {
  value: RatingValue | null
  onChange: (value: RatingValue | null) => void
  isDisabled?: boolean
}

const RatingInput: React.FC<RatingInputProps> = ({
  value,
  onChange,
  isDisabled,
}) => {
  const handleClick = (rating: RatingValue) => {
    if (isDisabled) return
    onChange(value === rating ? null : rating)
  }

  return (
    <Box w='fit-content'>
      <HStack spacing={3}>
        {([1, 2, 3, 4, 5] as RatingValue[]).map((rating) => {
          const isSelected = value === rating
          const style = RATING_STYLES[rating]
          return (
            <Button
              key={rating}
              w='52px'
              h='52px'
              minW='52px'
              borderRadius='10px'
              fontSize='lg'
              fontWeight='bold'
              variant='outline'
              isDisabled={isDisabled}
              bg={isSelected ? style.bg : 'white'}
              borderColor={isSelected ? style.border : 'gray.200'}
              color={isSelected ? style.color : 'gray.500'}
              borderWidth='2px'
              _hover={
                isDisabled ? {} : { bg: isSelected ? style.bg : 'gray.50' }
              }
              onClick={() => handleClick(rating)}
            >
              {rating}
            </Button>
          )
        })}
      </HStack>
      <Flex justify='space-between' mt={1}>
        <Text fontSize='xs' color='gray.500'>
          Poor
        </Text>
        <Text fontSize='xs' color='gray.500'>
          Excellent
        </Text>
      </Flex>
      {value && (
        <Text mt={3} fontSize='sm' color={RATING_STYLES[value].color}>
          {FEEDBACK_MESSAGES[value]}
        </Text>
      )}
    </Box>
  )
}

export default RatingInput
